/**
 * Who has not filed for the period (Round 2, item 8).
 *
 * The on-screen half of the item; the other half is an automatic broadcast
 * (`jobs.broadcastNotFiled`) that writes to the same people on a schedule.
 * Both read the same predicate on the server, so this page and the message
 * somebody receives cannot disagree about whether they filed.
 *
 * "Not filed" means no claim for the period that still stands — a voided claim
 * does not count, because voiding it is the statement that it should not.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { claimablePeriods, periodLabel } from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { DataTable, type Column } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { ErrorNote, QueuePanel } from '@/components/QueueKit';
import { LoadingBlock } from '@/components/Spinner';
import { useToast } from '@/components/Toast';
import { downloadWorkbook, todayStamp, type CellValue } from '@/components/admin/sheets';
import '@/styles/pages.css';

interface NotFiledEmployee {
  id: string;
  empId: string;
  name: string;
  email: string;
  department: string;
  position: string;
}

interface NotFiledResponse {
  period: string;
  employees: NotFiledEmployee[];
}

export function NotFiledPage(): ReactNode {
  const { toast } = useToast();
  const periods = useMemo(() => claimablePeriods(), []);
  const [period, setPeriod] = useState(periods[0] ?? '');
  const [data, setData] = useState<NotFiledResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await api.financeReview.notFiled<NotFiledResponse>({ period }));
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [period]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: ReadonlyArray<Column<NotFiledEmployee>> = useMemo(
    () => [
      { key: 'empId', header: 'Employee ID', cell: (r) => r.empId },
      { key: 'name', header: 'Name', cell: (r) => <b>{r.name}</b> },
      { key: 'department', header: 'Department', cell: (r) => r.department },
      { key: 'position', header: 'Position', cell: (r) => r.position },
      { key: 'email', header: 'Email', cell: (r) => r.email },
    ],
    [],
  );

  const exportList = useCallback(async (): Promise<void> => {
    const rows: CellValue[][] = [
      ['Employee ID', 'Name', 'Department', 'Position', 'Email'],
      ...(data?.employees ?? []).map((e) => [e.empId, e.name, e.department, e.position, e.email]),
    ];
    await downloadWorkbook(`not-filed-${period}-${todayStamp()}.xlsx`, [
      { name: 'Not filed', rows },
    ]);
    toast(`Exported ${data?.employees.length ?? 0} employee(s).`, 'ok');
  }, [data, period, toast]);

  if (data === null && error === null) return <LoadingBlock label="Checking who has filed…" />;

  return (
    <>
      <ErrorNote message={error} />

      <QueuePanel
        title="Not yet filed"
        count={data?.employees.length ?? 0}
        actions={
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => void exportList()}>
            ⬇ Export
          </button>
        }
      >
        <div className="field" style={{ maxWidth: 260 }}>
          <label htmlFor="nf-period">Period</label>
          <select id="nf-period" value={period} onChange={(e) => setPeriod(e.target.value)}>
            {periods.map((p) => (
              <option key={p} value={p}>
                {periodLabel(p)}
              </option>
            ))}
          </select>
        </div>

        <p className="hint">
          Active employees with Requestor access who have no standing claim for{' '}
          {periodLabel(data?.period ?? period)}. A voided claim does not count as having filed.
          These same people are emailed automatically on the day of the period Finance sets.
        </p>

        <DataTable
          rows={data?.employees ?? []}
          columns={columns}
          rowKey={(r) => r.id}
          empty={
            <EmptyState icon="✅" title="Everyone has filed for this period." />
          }
        />
      </QueuePanel>
    </>
  );
}
